import React, { useState } from "react";
import { Box, Image, Text, useToast } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import useViewPortWidth from "src/utils/use-viewport-width";
import {
  useAskSalahSubscription,
  useAskSalahSubscriptionActions,
  useAskSalahAccessGuard,
} from "src/hooks/askSalah";
import { checkFreeTrialSessions } from "src/api/askSalah";
import { SUBSCRIPTION_ROUTE, ASK_SALAH_SUBSCRIPTION_ROUTE } from "src/routePaths";
import type { AskSalahSubscription } from "src/types/askSalah";

/** Keeps the portal tab on /portal; opens the SPA route in the tab opened on click (or a new tab). */
function openAppRouteInNewTab(
  path: string,
  preOpened: Window | null,
  fallbackPush: (p: string) => void
) {
  const url = `${window.location.origin}${path}`;
  if (preOpened && !preOpened.closed) {
    preOpened.location.href = url;
    return;
  }
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    fallbackPush(path);
  }
}

const ASButton: React.FC<{}> = () => {
  const [isHovered, setIsHovered] = useState(false);
  const { viewPortWidth } = useViewPortWidth();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const toast = useToast();

  // Compensate for parent scale transform (0.85 for medium screens, 1.0 for large)
  const isMediumScreen = viewPortWidth >= 768 && viewPortWidth < 920;
  const scaleCompensation = isMediumScreen ? 1.176 * 1.75 : 1.75; // Increased from 1.5 to 1.75 for better vertical presence

  const {
    hasSAL125Subscription,
    hasAskSalahAccess,
    planType,
    subscription,
    isActive,
    refreshSubscription,
    isLoading,
  } = useAskSalahSubscription();

  const { canAccess, isExpired } = useAskSalahAccessGuard();
  
  // Additional explicit expiration check using subscription object directly
  // This ensures we catch expiration even if hooks haven't updated
  const isExplicitlyExpired = !!(
    subscription && 
    new Date(subscription.endDate) <= new Date()
  );

  const { accessAskSalah, isProcessing } = useAskSalahSubscriptionActions();

  const handleClick = async (e: React.MouseEvent) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
      e.preventDefault();
    }
    e.stopPropagation();
    
    // CRITICAL: Open window IMMEDIATELY before any async operations
    // This ensures the browser sees it as a direct user interaction
    // and doesn't block it as a popup (especially important for mobile)
    const newWindow = window.open('', '_blank');
    
    console.log('[ASButton] Click detected - Initial State:', {
      hasSAL125Subscription,
      hasAskSalahAccess,
      isExpired,
      canAccess,
      planType,
      subscription,
      isActive,
      isExplicitlyExpired,
      isLoading,
    });

    // IMPORTANT: Always refresh subscription data before checking access
    // This ensures we have the latest data, especially after admin approval
    console.log('[ASButton] Refreshing subscription data...');
    let freshSubscription: AskSalahSubscription | null = null;
    try {
      freshSubscription = await refreshSubscription();
      console.log('[ASButton] Fresh subscription after refresh:', freshSubscription);
      
      // Re-evaluate with fresh data
      const freshIsActive = !!(
        freshSubscription &&
        freshSubscription.status === 'active' &&
        new Date(freshSubscription.endDate) > new Date()
      );
      
      console.log('[ASButton] Fresh subscription check:', {
        freshSubscription,
        freshIsActive,
        status: freshSubscription?.status,
        endDate: freshSubscription?.endDate,
        now: new Date().toISOString(),
      });

      // If we now have an active subscription but the hook hasn't updated yet,
      // we need to proceed with the access flow using the fresh data
      if (freshIsActive && !hasAskSalahAccess) {
        console.log('[ASButton] Active subscription found after refresh! Proceeding with access...');
        // Continue to access flow below - the fresh subscription will be used
      } else if (!freshSubscription) {
        console.log('[ASButton] No subscription found after refresh');
      } else {
        console.log('[ASButton] Subscription found but not active:', {
          status: freshSubscription.status,
          endDate: freshSubscription.endDate,
          isExpired: new Date(freshSubscription.endDate) <= new Date(),
        });
      }
    } catch (error) {
      console.error('[ASButton] Error refreshing subscription:', error);
      // Continue with existing checks if refresh fails
    }

    // IMPORTANT: Check subscription status BEFORE generating token
    // This prevents unnecessary API calls and provides better UX
    // Use fresh subscription data if available, otherwise use hook data
    const currentSubscription = freshSubscription || subscription;
    const currentIsActive = freshSubscription 
      ? (freshSubscription.status === 'active' && new Date(freshSubscription.endDate) > new Date())
      : isActive;
    const currentHasAccess = freshSubscription ? currentIsActive : hasAskSalahAccess;

    // Scenario 1: No SAL125 subscription → SAL125 subscription page in a new tab
    if (!hasSAL125Subscription) {
      toast({
        title: formatMessage({
          id: 'askSalah.error.noSAL125',
          defaultMessage: 'SAL125 Subscription Required',
        }),
        description: formatMessage({
          id: 'askSalah.error.noSAL125.description',
          defaultMessage: 'Please subscribe to SAL125 to access AskSalah.',
        }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      openAppRouteInNewTab(SUBSCRIPTION_ROUTE, newWindow, history.push);
      return;
    }

    // Scenario 2: Check subscription expiration (only if subscription exists)
    // Use fresh subscription data if available
    if (currentSubscription) {
      const currentIsExpired = new Date(currentSubscription.endDate) <= new Date();
      if (currentIsExpired || !currentIsActive) {
        console.log('[ASButton] Subscription expired or inactive:', {
          isExpired: currentIsExpired,
          isActive: currentIsActive,
          endDate: currentSubscription.endDate,
          now: new Date().toISOString(),
        });
        toast({
          title: formatMessage({
            id: 'askSalah.error.expired',
            defaultMessage: 'Subscription Expired',
          }),
          description: formatMessage({
            id: 'askSalah.error.expired.description',
            defaultMessage: 'Your AskSalah subscription has expired. Please renew to continue.',
          }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        openAppRouteInNewTab(ASK_SALAH_SUBSCRIPTION_ROUTE, newWindow, history.push);
        return;
      }
    }

    // Scenario 3: No Ask Salah subscription
    // Use fresh subscription check if available
    if (!currentHasAccess) {
      console.log('[ASButton] No Ask Salah subscription, redirecting to subscription page...');
      openAppRouteInNewTab(ASK_SALAH_SUBSCRIPTION_ROUTE, newWindow, history.push);
      return;
    }
    
    // Scenario 4: Safety check - cannot access (double verification)
    // Use fresh data for this check too
    if (!currentIsActive) {
      console.log('[ASButton] Cannot access (safety check), redirecting...');
      openAppRouteInNewTab(ASK_SALAH_SUBSCRIPTION_ROUTE, newWindow, history.push);
      return;
    }

    // Scenario 5: All checks passed - user has active subscription
    // Now check sessions if trial user, then generate token
    const currentPlanType = freshSubscription?.planType || planType;
    const isPaidAskSalahPlan =
      currentPlanType === 'basic' || currentPlanType === 'premium';

    if (!isPaidAskSalahPlan && currentPlanType === 'trial') {
      try {
        const sessionCheck = await checkFreeTrialSessions();
        
        if (sessionCheck.status === 'success' && sessionCheck.data) {
          if (sessionCheck.data.session_remaining === 0) {
            // Paid upgrade may have just completed — re-fetch before blocking access
            const retrySubscription = await refreshSubscription();
            const retryPlan = retrySubscription?.planType;
            const hasPaidAfterRetry =
              retrySubscription &&
              retrySubscription.status === 'active' &&
              new Date(retrySubscription.endDate) > new Date() &&
              (retryPlan === 'basic' || retryPlan === 'premium');

            if (!hasPaidAfterRetry) {
              toast({
                description: formatMessage({
                  id: 'askSalah.modal.trialExhausted.description',
                  defaultMessage: 'You\'ve reached the limit of your free trial sessions. Upgrade to keep using AskSalah.',
                }),
                status: 'warning',
                duration: 5000,
                isClosable: true,
              });
              openAppRouteInNewTab(ASK_SALAH_SUBSCRIPTION_ROUTE, newWindow, history.push);
              return;
            }
          }
          // If sessions remaining > 0, continue with normal flow
        } else {
          // API call failed - fail open: allow access and let Ask Salah service enforce limits
          console.warn('Failed to check trial sessions, allowing access:', sessionCheck.message);
        }
      } catch (error) {
        // Network or other errors - fail open: allow access
        console.error('Error checking trial sessions:', error);
      }
    }

    // Scenario 6: Generate token and redirect to external Ask Salah site
    // Only reaches here if all subscription checks passed
    console.log('[ASButton] All checks passed, generating token...');
    try {
      // Pass the pre-opened window to accessAskSalah
      const result = await accessAskSalah(newWindow);
      
      if (!result.success) {
        console.error('[ASButton] Token generation failed:', result.error);
        
        // Close the window if token generation failed
        if (newWindow && !newWindow.closed) {
          newWindow.close();
        }
        
        // Check if error is subscription-related
        const errorMsg = result.error || '';
        const isSubscriptionError = 
          errorMsg.includes('subscription') || 
          errorMsg.includes('403') ||
          errorMsg.includes('Forbidden');
        
        if (isSubscriptionError) {
          // Backend says no subscription - subscription page in a new tab (token flow may have closed preOpened)
          toast({
            title: formatMessage({
              id: 'askSalah.error.expired',
              defaultMessage: 'Subscription Expired',
            }),
            description: formatMessage({
              id: 'askSalah.error.expired.description',
              defaultMessage: 'Your AskSalah subscription has expired. Please renew to continue.',
            }),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          openAppRouteInNewTab(ASK_SALAH_SUBSCRIPTION_ROUTE, newWindow, history.push);
        } else {
          // Other error
          toast({
            title: formatMessage({
              id: 'askSalah.error.accessFailed',
              defaultMessage: 'Access Failed',
            }),
            description: result.error || formatMessage({ id: 'wentWrong' }),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error: any) {
      console.error('[ASButton] Exception during access:', error);
      
      // Close the window if an exception occurred
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
      
      toast({
        title: formatMessage({
          id: 'askSalah.error.accessFailed',
          defaultMessage: 'Access Failed',
        }),
        description: error.message || formatMessage({ id: 'wentWrong' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getHoverText = () => {
    if (!hasSAL125Subscription) {
      return formatMessage({ id: 'askSalah.button.subscribe', defaultMessage: 'AskSalah' });
    }
    if (!hasAskSalahAccess) {
      return formatMessage({ id: 'askSalah.button.getStarted', defaultMessage: 'Get Started' });
    }
    return formatMessage({ id: 'askSalah.button.access', defaultMessage: 'Access AskSalah' });
  };

  return (
    <Box
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      transform="translateY(10%)"
      overflow="visible"
      cursor={isProcessing || isLoading ? "wait" : "pointer"}
      onClick={handleClick}
      pointerEvents={isProcessing || isLoading ? "none" : "auto"}
    >
      {/* Default AS icon - visible when not hovered */}
      {/* @ts-ignore */}
      <Image
        src="/as_icon.png"
        alt="as icon"
        width="40px"
        height="40px"
        objectFit="contain"
        position="relative"
        left="50%"
        transform="translateX(-50%)"
        opacity={isHovered ? "0" : "1"}
        transition="opacity 0.5s ease-in-out"
        zIndex={1}
      />
      {/* Ask Salah logo - visible when hovered */}
      <img
        src="/ask-salah-logo.png"
        alt="Ask Salah logo"
        className="ask-salah-logo-hover"
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: `translate(-50%, -30%) scale(${scaleCompensation})`,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

export default ASButton;
